/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import type { CSSProperties } from 'react';

import { COLORS, FONT } from '@/Client/Partials/CloseoutReviewFlow/closeoutTokens';
import { usePhotoUriForLayoutSize } from '@kaiizen/kai-pro-api-react-state';
import { type ScanPhoto } from '@kaiizen/kai-scan-template-system';

import { pxToRem } from '@/Client/Styles/pxToRem';
import { useProgressAlt } from './progressAltContext';
import { GAP_PX, TILE_RADIUS_PX } from './progressAltMetrics';
import { getColumnKey, getPhotoCountLabel, type ProgressAltColumn } from './progressAltModel';
import { FOCUS_RING_STYLE } from './progressAltStyles';

const CELL_STYLE = css`
  label: PROGRESS_ALT_CELL_STYLE;
  min-width: 0;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: ${GAP_PX}px;
`;

const TILE_STYLE = css`
  label: PROGRESS_ALT_TILE_STYLE;
  position: relative;
  min-width: 0;
  overflow: hidden;
  border: 1px solid ${COLORS.stroke};
  border-radius: ${TILE_RADIUS_PX}px;
  background: ${COLORS.surfaceMuted};
`;

/** Nothing was photographed here: the dashed outline says it, no fill needed. */
const TILE_EMPTY_STYLE = css`
  border-style: dashed;
  background: transparent;
`;

const TILE_CLICKABLE_STYLE = css`
  padding: 0;
  font: inherit;
  color: inherit;
  text-align: inherit;
  cursor: pointer;

  &:hover {
    border-color: ${COLORS.midtone};
  }

  ${FOCUS_RING_STYLE}
`;

const TILE_IMAGE_STYLE = css`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

/** A collapsed row keeps its shape but trades photos for a solid block and a count. */
const TILE_COLLAPSED_FILLED_STYLE = css`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${COLORS.sunken};
`;

const PHOTO_COUNT_STYLE = css`
  label: PROGRESS_ALT_PHOTO_COUNT_STYLE;
  padding: 2px 6px;
  border: 1px solid ${COLORS.stroke};
  border-radius: 0;
  background: ${COLORS.white};
  color: ${COLORS.ink};
  font-family: ${FONT.mono};
  font-size: ${pxToRem(9)};
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const PHOTO_COUNT_OVERLAY_STYLE = css`
  position: absolute;
  top: 6px;
  right: 6px;
`;

const getTileWidthStyle = (width: number | undefined): CSSProperties => width == null ? { flex: 1 } : { flex: `0 0 ${width}px`, width };

/**
 * Photos carry no caption: the column header already names the group or task, so repeating it on
 * every tile only buys a scrim over the picture. A tile becomes a button whenever there is
 * somewhere for a click to go.
 */
const PhotoTile = ({ photoId, alt, photoCount, width, actionLabel, onClick }: {
  photoId?: string;
  alt: string;
  /** Counts the photos this tile stands in for, when it stands in for more than one. */
  photoCount?: number;
  width?: number;
  actionLabel?: string;
  onClick?: () => void;
}) => {
  const { tileSize } = useProgressAlt();
  // Tiles are wider than 4:3, so the column width is what the photo's long side has to fill.
  const imageUrl = usePhotoUriForLayoutSize(photoId, tileSize.columnWidthPx);
  const content = (
    <>
      {
        /*
        A scan's grid runs to hundreds of tiles across a timeline nobody scrolls all of, so the
        browser is left to fetch each photo when it is nearly in view rather than all of them at
        once. The tile is a fixed size either way, so nothing shifts as they arrive.
      */
      }
      {imageUrl ? <img css={TILE_IMAGE_STYLE} src={imageUrl} alt={alt} loading='lazy' decoding='async' /> : null}
      {photoCount && photoCount > 1
        ? (
          <span css={[PHOTO_COUNT_STYLE, PHOTO_COUNT_OVERLAY_STYLE]} data-testid='progress-alt-photo-count'>
            {getPhotoCountLabel(photoCount)}
          </span>
        )
        : null}
    </>
  );
  const tileStyles = [TILE_STYLE, !photoId && TILE_EMPTY_STYLE];

  return onClick
    ? (
      <button
        type='button'
        css={[...tileStyles, TILE_CLICKABLE_STYLE]}
        style={getTileWidthStyle(width)}
        aria-label={actionLabel}
        data-progress-alt-cell='true'
        onClick={onClick}
      >
        {content}
      </button>
    )
    : (
      <div css={tileStyles} style={getTileWidthStyle(width)} data-progress-alt-cell='true'>
        {content}
      </div>
    );
};

/** A collapsed row shows only whether a scope was photographed, and how often. */
const CollapsedTile = ({ photoCount, onClick }: { photoCount: number; onClick: () => void }) => (
  <button
    type='button'
    css={[TILE_STYLE, !photoCount && TILE_EMPTY_STYLE, !!photoCount && TILE_COLLAPSED_FILLED_STYLE, TILE_CLICKABLE_STYLE]}
    style={{ flex: 1 }}
    aria-label='Expand row'
    data-progress-alt-cell='true'
    data-progress-alt-collapsed='true'
    onClick={onClick}
  >
    {photoCount ? <span css={PHOTO_COUNT_STYLE} data-testid='progress-alt-photo-count'>{getPhotoCountLabel(photoCount)}</span> : null}
  </button>
);

/**
 * One column's photos for one time bucket. Collapsed shows only the newest, counting the rest in a
 * small box; expanded lays every photo of the bucket out side by side in whatever order they
 * arrived — they all share the row's date, so nothing is gained by ranking them.
 */
export const ProgressAltCell = ({
  column,
  photos,
  expanded,
  collapsed,
  onExpandRow,
  onExpandColumn,
  onOpenPhoto,
}: {
  column: ProgressAltColumn;
  photos: readonly ScanPhoto[];
  expanded: boolean;
  collapsed: boolean;
  onExpandRow: () => void;
  onExpandColumn: () => void;
  /** The grid knows which cell this is; the cell only has to say which of its photos was clicked. */
  onOpenPhoto: (photoId: string) => void;
}) => {
  const { tileSize } = useProgressAlt();
  // A collapsed row answers a click by opening, whatever was clicked and whether or not it holds a
  // photo — reading the row is the only thing anyone can want from it in that state.
  if (collapsed) return <CollapsedTile photoCount={photos.length} onClick={onExpandRow} />;

  const visiblePhotos = expanded ? photos : photos.slice(-1);
  if (!visiblePhotos.length) {
    return (
      <div css={CELL_STYLE}>
        <PhotoTile alt='' />
      </div>
    );
  }

  // A tile standing in for several photos opens the column rather than any one of them.
  const standsForSeveral = !expanded && photos.length > 1;
  return (
    <div css={CELL_STYLE}>
      {visiblePhotos.map(photo => (
        <PhotoTile
          key={`${getColumnKey(column)}:${photo.photoId}`}
          photoId={photo.photoId}
          alt={`${column.title} photo`}
          photoCount={expanded ? undefined : photos.length}
          width={expanded ? tileSize.columnWidthPx : undefined}
          actionLabel={standsForSeveral
            ? `Show all ${photos.length} photos for ${column.title}`
            : `Open photo for ${column.title}`}
          onClick={standsForSeveral ? onExpandColumn : () => onOpenPhoto(photo.photoId)}
        />
      ))}
    </div>
  );
};
